/**
 * Workflow admin hooks (Phase 2).
 *
 * Talks to the /api/v2/workflows surface added in Phase 2. The engine is
 * dormant in production until an admin binds a definition to an AssetClass
 * via these hooks. See Docs/Features/DynamicFlows/.
 *
 * Designer round-trip (Phase 3) reuses the same import/validate hooks —
 * the React Flow output serializes to the same JSON contract the
 * /import endpoint consumes.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

// ============================================================
// Types
// ============================================================

export interface WorkflowDefinitionSummary {
  id: number;
  code: string;
  name: string;
  description: string | null;
  version: number;
  scope: string;
  isTemplate: boolean;
  isActive: boolean;
  stateCount: number;
  transitionCount: number;
}

export interface WorkflowStateDetail {
  id: number;
  code: string;
  name: string;
  isInitial: boolean;
  isTerminal: boolean;
  displayOrder: number;
  uiIcon: string | null;
  uiBadgeColor: string | null;
}

export interface WorkflowTransitionDetail {
  id: number;
  code: string;
  name: string;
  description: string | null;
  fromStateCode: string;
  toStateCode: string;
  requiredPermissionModule: string | null;
  requiredPermissionAction: string | null;
  requiredRoleCode: string | null;
  formMetadataSchemaId: number | null;
  displayOrder: number;
  shipmentTemplateJson: string | null;
}

export interface WorkflowDefinitionDetail extends WorkflowDefinitionSummary {
  states: WorkflowStateDetail[];
  transitions: WorkflowTransitionDetail[];
}

export interface WorkflowBinding {
  id: number;
  entity: string;
  clientId: number | null;
  assetClassId: number;
  workflowDefinitionId: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  createdDate: string;
  createdBy: string;
}

export interface WorkflowImportError {
  code: string;
  message: string;
  path: string | null;
}

export interface WorkflowValidateResponse {
  ok: boolean;
  errors: WorkflowImportError[];
  /** P05 — non-blocking lint warnings; always present, possibly empty. */
  warnings: WorkflowImportError[];
}

export interface WorkflowImportSuccess {
  ok: true;
  workflowDefinitionId: number;
  code: string;
  version: number;
  /** P05 — non-blocking lint warnings raised during import. */
  warnings: WorkflowImportError[];
}

export interface WorkflowImportFailure {
  ok: false;
  errors: WorkflowImportError[];
  /** P05 — warnings still surface on failure (so the author can see them
   *  alongside the blocking errors that need fixing). */
  warnings: WorkflowImportError[];
}

export type WorkflowImportResponse = WorkflowImportSuccess | WorkflowImportFailure;

// ============================================================
// Queries
// ============================================================

const KEY_DEFS = ['workflow-definitions'] as const;
const KEY_DEF_DETAIL = (id: number | null) => ['workflow-definition', id] as const;
const KEY_BINDINGS = ['workflow-bindings'] as const;
const KEY_VALID_TX = (assetId: string | null) => ['workflow-valid-transitions', assetId] as const;
const KEY_DYNAMIC_MODULES = ['dynamic-modules'] as const;

// ============================================================
// Phase 11 — Tenant-authored operator modules (dynamic left nav)
// ============================================================

/** Module type catalog from the backend (closed vocabulary). */
export type DynamicModuleType =
  | 'client_hub'
  | 'asset_state_queue'
  | 'shipment_queue'
  | 'custom_report_link'
  // PalletCenter — RFP/PO queue driven by WorkflowOrders. Client-centric
  // nav restructure adds a renderer for this type (unlocked + client-locked).
  | 'order_queue';

/** Nav-group bucket the React app maps onto its existing left-nav sections. */
export type DynamicModuleNavGroup = 'operations' | 'clientManagement' | 'reports';

export interface DynamicModule {
  code: string;
  name: string;
  description: string | null;
  displayOrder: number;
  navGroup: DynamicModuleNavGroup;
  uiIcon: string | null;
  uiBadgeColor: string | null;
  moduleType: DynamicModuleType;
  permissionModule: string | null;
  permissionAction: string | null;
  workflowDefinitionId: number;
  /** Type-specific config as raw JSON string; page templates parse it on render. */
  configJson: string | null;
  /**
   * P08 — pending-work count for this module's nav badge. Present only
   * when the hook requested `?includeCounts=true`. Always null for
   * custom_report_link or when the backend's count query failed.
   */
  count?: number | null;
}

/**
 * Client-centric nav restructure — parsed shape of a module's `configJson`
 * that the nav + workspace layers care about. Everything is optional; the
 * parser never throws. `scope: 'client'` marks a module that must NOT
 * appear in the main left nav and instead surfaces inside the per-client
 * workspace (`/clients-hub/:clientId`).
 */
export interface ParsedModuleConfig {
  scope?: 'client' | 'tenant' | string;
  [key: string]: unknown;
}

/**
 * Parse a module's `configJson` defensively. Returns an empty object on
 * null / invalid JSON so callers can read fields without try/catch. Not a
 * hook — safe to call inside `useMemo` at any call site.
 */
export function parseModuleConfig(m: Pick<DynamicModule, 'configJson'>): ParsedModuleConfig {
  if (!m.configJson) return {};
  try {
    const parsed = JSON.parse(m.configJson) as ParsedModuleConfig;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * True when the module declares `scope: 'client'` in its config — meaning
 * it is a per-client workspace tab, not a top-level nav destination. The
 * nav merger excludes these; the client workspace includes them.
 */
export function isClientScoped(m: Pick<DynamicModule, 'configJson'>): boolean {
  return parseModuleConfig(m).scope === 'client';
}

/**
 * Client-centric nav restructure — server-provided Tailwind color token
 * (`uiBadgeColor`) → badge classes. Mirrors AssetTraceTimeline's mapping so
 * state chips look identical across the dynamic surface. Exported so the
 * hub landing, client workspace, and asset-state summary chips share it.
 */
export function stateBadgeClass(color: string | null | undefined): string {
  const map: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    green: 'bg-green-100 text-green-700',
    violet: 'bg-violet-100 text-violet-700',
    amber: 'bg-amber-100 text-amber-700',
    orange: 'bg-orange-100 text-orange-700',
    cyan: 'bg-cyan-100 text-cyan-700',
    red: 'bg-red-100 text-red-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    gray: 'bg-gray-100 text-gray-700',
  };
  return map[color ?? ''] ?? 'bg-slate-100 text-slate-700';
}

/** Asset row returned by the per-module assets endpoint. */
export interface ModuleAsset {
  assetId: string;
  name: string | null;
  locationId: number;
  locationName: string | null;
  clientId: number | null;
  currentStateCode: string | null;
  currentStateName: string | null;
  lastTransitionAt: string;
  /**
   * PalletCenter G — the client that owns this asset (resolved from the
   * EPC prefix rules by C1). Both null on legacy tenants that have no
   * owner rules configured, so the queue's owner column stays hidden.
   */
  ownerClientId: number | null;
  ownerClientName: string | null;
}

/** Per-state breakdown bundled into each ClientHubItem card. */
export interface ClientHubStateCount {
  stateId: number;
  stateCode: string | null;
  stateName: string | null;
  count: number;
  /**
   * Client-centric nav restructure — optional badge color token so the
   * client workspace can color the byState chips consistently with the
   * assets/summary chips. Absent on legacy responses; the chip helper
   * falls back to the neutral slate style.
   */
  uiBadgeColor?: string | null;
}

/** Per-state breakdown row returned by the assets/summary endpoint. */
export interface ModuleAssetStateSummary {
  stateCode: string | null;
  stateName: string | null;
  uiBadgeColor: string | null;
  count: number;
}

/**
 * Client card on the client_hub template.
 *
 * Client-centric nav restructure — the /clients endpoint now enriches each
 * row with the aggregates the hub landing + client workspace KPIs need
 * (openOrders, inTransitShipments). `byState[].uiBadgeColor` is likewise
 * added so the workspace can color state chips like the rest of the
 * dynamic surface. All new fields are optional-safe: they default to 0 /
 * null on tenants whose backend hasn't been upgraded, so the legacy
 * client_hub card keeps rendering.
 */
export interface ClientHubItem {
  clientId: number;
  name: string;
  isActive: boolean;
  totalAssets: number;
  /** Open WorkflowOrders (RFP/PO) touching this client. */
  openOrders: number;
  /** Batches currently in transit to / from this client's network. */
  inTransitShipments: number;
  lastActivityAt: string | null;
  byState: ClientHubStateCount[];
}

/**
 * Reads the tenant's merged module list. Returns an empty array when:
 *  - the backend feature flag is off (404 → caught and treated as empty),
 *  - the tenant has no workflow bindings,
 *  - none of the bound workflows declare modules[].
 *
 * In every "empty" case the React left-nav merger leaves the hardcoded
 * TOCA items untouched, which is the explicit design — Phase 10-14 is
 * additive-only per the user-confirmed scope.
 */
export function useDynamicModules() {
  return useQuery({
    queryKey: KEY_DYNAMIC_MODULES,
    // Don't pound the endpoint on every nav re-render; the modules
    // change only when an admin imports / binds a workflow.
    staleTime: 60_000,
    // P08 — refresh badges every minute so counts stay roughly live
    // without flooding the API. Transition-fire mutations also invalidate
    // ['dynamicModules'], so the badges update immediately after an
    // operator action.
    refetchInterval: 60_000,
    queryFn: async () => {
      try {
        const res = await api.get<{ items: DynamicModule[] }>(
          '/api/v2/workflows/modules?includeCounts=true',
        );
        return res.data.items ?? [];
      } catch (e: any) {
        // 404 = feature flag off OR SGS tenant. Both should fall through
        // to the legacy nav silently.
        if (e?.response?.status === 404) return [] as DynamicModule[];
        throw e;
      }
    },
  });
}

/**
 * Client-centric nav restructure — resolves the tenant's client-centric
 * navigation shape from the dynamic module catalog:
 *   - `hubModule`: the first `client_hub` module (drives the /clients-hub
 *     landing variant + the client name/summary source), or null.
 *   - `clientScopedModules`: modules flagged `scope: 'client'`, sorted by
 *     displayOrder, that render as tabs inside the per-client workspace.
 *   - `isDynamicHub`: true when EITHER a client_hub module OR any
 *     client-scoped module exists — the single gate that flips ClientsHub /
 *     ClientDashboard pages to their workflow variants. TOCA/SGS tenants
 *     resolve to false and keep the legacy pages verbatim.
 *
 * Derived from the already-cached useDynamicModules query, so it adds no
 * extra network traffic.
 */
export interface ClientHubNav {
  hubModule: DynamicModule | null;
  clientScopedModules: DynamicModule[];
  isDynamicHub: boolean;
  isLoading: boolean;
}

export function useClientHubNav(): ClientHubNav {
  const { data: modules = [], isLoading } = useDynamicModules();

  return useMemo(() => {
    const clientScopedModules = modules
      .filter((m) => isClientScoped(m))
      .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));
    // The client-centric hub is opted into by flagging modules with
    // scope:"client" — a client_hub module ALONE does not switch the
    // /clients-hub surface. (The dev TOCA tenant carries the INFRA example
    // workflow, whose hospital hub is a client_hub module: keying on the
    // module type replaced TOCA's legacy clients hub with an empty
    // hospital-category hub.)
    const hasClientCentricNav = clientScopedModules.length > 0;
    const hubModule = hasClientCentricNav
      ? modules.find((m) => m.moduleType === 'client_hub') ?? null
      : null;
    return {
      hubModule,
      clientScopedModules,
      isDynamicHub: hasClientCentricNav,
      isLoading,
    };
  }, [modules, isLoading]);
}

/**
 * P11 — One row of the workflow import audit log. Shape matches
 * GET /api/v2/workflows/definitions/{code}/import-history (no snapshot).
 */
export interface WorkflowImportLogRow {
  id: number;
  workflowDefinitionId: number | null;
  code: string;
  version: number;
  userId: number;
  /** Resolved importer name; optional so an older API degrades to the id. */
  userName?: string | null;
  importDate: string;
  source: string;
  jsonHash: string;
  diffSummaryJson: string;
  outcome: 'success' | 'rejected' | string;
}

/**
 * P11 — Lineage import history for a single definition code (all
 * versions, newest first). Returns an empty list on 404 so the designer
 * card silently collapses when feature flag is off or the definition is
 * brand-new (no imports yet).
 */
export function useWorkflowImportHistory(code: string | null | undefined, limit = 20) {
  return useQuery({
    queryKey: ['workflowImportHistory', code, limit],
    enabled: !!code,
    staleTime: 30_000,
    queryFn: async (): Promise<WorkflowImportLogRow[]> => {
      try {
        const res = await api.get<{ items: WorkflowImportLogRow[] }>(
          `/api/v2/workflows/definitions/${encodeURIComponent(code!)}/import-history?limit=${limit}`,
        );
        return res.data.items ?? [];
      } catch (e: any) {
        if (e?.response?.status === 404) return [];
        throw e;
      }
    },
  });
}

/**
 * P11 — Single import log row including the JSON snapshot, for the
 * download / inspection action on the history card.
 */
export interface WorkflowImportLogDetail extends WorkflowImportLogRow {
  jsonSnapshot: string | null;
}

export async function fetchWorkflowImportLogDetail(id: number): Promise<WorkflowImportLogDetail> {
  const res = await api.get<WorkflowImportLogDetail>(`/api/v2/workflows/import-log/${id}`);
  return res.data;
}

/**
 * P06 — One template summary row from /api/v2/workflows/templates.
 */
export interface WorkflowTemplateSummary {
  id: number;
  code: string;
  name: string;
  description: string | null;
  tags: string[];
  stateCount: number;
  transitionCount: number;
  moduleCount: number;
}

/**
 * P06 — Template gallery list. Empty array on 404 (workflows feature
 * flag off, SGS deployment, or genuinely no templates seeded) so the
 * gallery button on the workflows list page can render conditionally.
 */
export function useWorkflowTemplates() {
  return useQuery({
    queryKey: ['workflowTemplates'],
    staleTime: 5 * 60_000, // templates rarely change mid-session
    queryFn: async (): Promise<WorkflowTemplateSummary[]> => {
      try {
        const res = await api.get<{ items: WorkflowTemplateSummary[] }>(
          '/api/v2/workflows/templates',
        );
        return res.data.items ?? [];
      } catch (e: any) {
        if (e?.response?.status === 404) return [];
        throw e;
      }
    },
  });
}

/**
 * P06 — Clone the named template into the caller's tenant. Returns the
 * new definition id on success. Surfaces 409 via the error shape so the
 * dialog can show a friendly "code already exists" message.
 */
export function useCloneWorkflowTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { code: string; newCode?: string; newName?: string }) => {
      const res = await api.post<{
        ok: boolean;
        workflowDefinitionId: number;
        code: string;
      }>(`/api/v2/workflows/templates/${encodeURIComponent(input.code)}/clone`, {
        newCode: input.newCode,
        newName: input.newName,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflowDefinitions'] });
    },
  });
}

/** Single-module lookup used by the catch-all DynamicModulePage on cold loads. */
export function useDynamicModule(code: string | null) {
  return useQuery({
    queryKey: ['dynamic-module', code],
    enabled: code !== null,
    queryFn: async () => {
      const res = await api.get<DynamicModule>(
        `/api/v2/workflows/modules/${encodeURIComponent(code!)}`
      );
      return res.data;
    },
  });
}

/**
 * Asset list for `asset_state_queue` templates and the `client_hub`
 * drill-down view. The backend resolves the module's state filter
 * server-side from its ConfigJson so the UI passes only the module
 * code + optional filters.
 *
 * @param clientId       location-scoped client filter (client_hub drilldown).
 * @param ownerClientId  PalletCenter G — filter to assets owned by a
 *                       specific client (resolved from EPC prefix rules).
 */
export function useModuleAssets(
  code: string | null,
  clientId?: number | null,
  ownerClientId?: number | null,
) {
  return useQuery({
    queryKey: ['dynamic-module-assets', code, clientId ?? null, ownerClientId ?? null],
    enabled: code !== null,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (clientId) params.set('clientId', String(clientId));
      if (ownerClientId) params.set('ownerClientId', String(ownerClientId));
      const qs = params.toString();
      const res = await api.get<{ items: ModuleAsset[]; total: number }>(
        `/api/v2/workflows/modules/${encodeURIComponent(code!)}/assets${qs ? `?${qs}` : ''}`
      );
      return res.data;
    },
  });
}

/**
 * Client-centric nav restructure — per-state asset summary for an
 * asset_state_queue module, optionally scoped to an owner client. Feeds
 * the stat-chip header rendered above the locked queue table in the
 * client workspace. Returns { items, total }.
 *
 * GET /api/v2/workflows/modules/{code}/assets/summary?ownerClientId=
 */
export interface ModuleAssetSummaryResponse {
  items: ModuleAssetStateSummary[];
  total: number;
}

export function useModuleAssetSummary(
  code: string | null,
  ownerClientId?: number | null,
) {
  return useQuery({
    queryKey: ['dynamic-module-asset-summary', code, ownerClientId ?? null],
    enabled: code !== null,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (ownerClientId) params.set('ownerClientId', String(ownerClientId));
      const qs = params.toString();
      const res = await api.get<ModuleAssetSummaryResponse>(
        `/api/v2/workflows/modules/${encodeURIComponent(code!)}/assets/summary${qs ? `?${qs}` : ''}`,
      );
      return res.data;
    },
  });
}

/**
 * PalletCenter I — one recent-activity row for the client_hub `trace`
 * section. Shape matches GET /api/v2/workflows/modules/{code}/trace.
 */
export interface ModuleTraceEvent {
  assetId: string;
  epc: string | null;
  occurredAt: string;
  transitionCode: string | null;
  description: string | null;
  userId: number | null;
}

/**
 * PalletCenter I — recent workflow activity for a client's assets, used
 * by the client_hub `trace` section. Disabled until a client is chosen
 * (drilldown) so the landing overview doesn't fetch it.
 */
export function useModuleTrace(
  code: string | null,
  clientId: number | null,
  take = 25,
) {
  return useQuery({
    queryKey: ['dynamic-module-trace', code, clientId, take],
    enabled: code !== null && clientId !== null,
    queryFn: async () => {
      const res = await api.get<{ items: ModuleTraceEvent[] }>(
        `/api/v2/workflows/modules/${encodeURIComponent(code!)}/trace?clientId=${clientId}&take=${take}`
      );
      return res.data.items ?? [];
    },
  });
}

/**
 * Designer-side dropdown: lists the tenant's location categories so
 * the `client_hub` module form can offer a picker instead of a free
 * text field.
 */
export function useLocationCategories() {
  return useQuery({
    queryKey: ['workflow-location-categories'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await api.get<{ items: Array<{ code: string; name: string }> }>(
        '/api/v2/workflows/location-categories'
      );
      return res.data.items ?? [];
    },
  });
}

/** Per-client aggregation for `client_hub`. */
export function useModuleClients(code: string | null) {
  return useQuery({
    queryKey: ['dynamic-module-clients', code],
    enabled: code !== null,
    queryFn: async () => {
      const res = await api.get<{ items: ClientHubItem[] }>(
        `/api/v2/workflows/modules/${encodeURIComponent(code!)}/clients`
      );
      return res.data.items;
    },
  });
}

export function useWorkflowDefinitions(activeOnly = true) {
  return useQuery({
    queryKey: [...KEY_DEFS, { activeOnly }],
    queryFn: async () => {
      const res = await api.get<{ items: WorkflowDefinitionSummary[]; total: number }>(
        `/api/v2/workflows/definitions?activeOnly=${activeOnly}`
      );
      return res.data;
    },
  });
}

export function useWorkflowDefinition(id: number | null) {
  return useQuery({
    queryKey: KEY_DEF_DETAIL(id),
    enabled: id !== null,
    queryFn: async () => {
      const res = await api.get<WorkflowDefinitionDetail>(`/api/v2/workflows/definitions/${id}`);
      return res.data;
    },
  });
}

/**
 * Phase 6 — Aggregates per-state asset counts grouped by the
 * `kpi_role` controlled vocabulary so the dashboard's
 * `WorkflowKpiCardGrid` can render workflow-aware tiles without
 * hardcoded byte values. Returns an empty tiles array when the
 * definition has no states tagged with a KPI role (the TOCA_DEFAULT
 * case — TOCA dashboards keep their existing byte-based tiles).
 *
 * Per ADR-013 / ADR-015 this endpoint 404s on SGS and is additive
 * to TOCA; see Docs/Features/DynamicFlows/14-reporting-and-dashboards.md.
 */
export interface KpiTile {
  kpiRole: string;
  label: string;
  count: number;
  stateIds: number[];
  stateCodes: string[];
  drilldownUrl: string;
}

export interface KpiSummaryResponse {
  workflowDefinitionId: number;
  tiles: KpiTile[];
}

export function useWorkflowKpiSummary(
  definitionId: number | null,
  options?: { clientId?: number | null; refetchInterval?: number }
) {
  const params = new URLSearchParams();
  if (options?.clientId) params.set('clientId', String(options.clientId));
  const qs = params.toString();
  return useQuery({
    queryKey: ['workflow-kpi-summary', definitionId, options?.clientId ?? null],
    enabled: definitionId !== null,
    refetchInterval: options?.refetchInterval,
    queryFn: async () => {
      const res = await api.get<KpiSummaryResponse>(
        `/api/v2/workflows/definitions/${definitionId}/kpi-summary${qs ? `?${qs}` : ''}`
      );
      return res.data;
    },
  });
}

export function useWorkflowBindings(params?: { assetClassId?: number; clientId?: number }) {
  const search = new URLSearchParams();
  if (params?.assetClassId) search.set('assetClassId', String(params.assetClassId));
  if (params?.clientId) search.set('clientId', String(params.clientId));
  const qs = search.toString();
  return useQuery({
    queryKey: [...KEY_BINDINGS, params ?? {}],
    queryFn: async () => {
      const res = await api.get<{ items: WorkflowBinding[] }>(
        `/api/v2/workflows/bindings${qs ? `?${qs}` : ''}`
      );
      return res.data.items;
    },
  });
}

/**
 * Returns the engine's valid-next-transitions list for an asset, or null when
 * the API responds 409 WORKFLOW_NOT_CONFIGURED — which is the normal Phase 1
 * production reality for any tenant that has not bound a workflow yet. The
 * caller treats null as "no engine workflow bound; render the legacy menu".
 */
export interface ValidTransition {
  code: string;
  name: string;
  toStateCode: string;
  formMetadataSchemaId: number | null;
  allowed: boolean;
  denyReasonCode: string | null;
}

/**
 * Snapshot of the asset's engine state — used by both the actions dropdown
 * and the asset-detail status badge so both surfaces agree on the current
 * workflow state. Sharing one query keeps things to a single network call.
 */
export interface AssetEngineSnapshot {
  currentStateCode: string;
  currentStateName: string;
  workflowDefinitionId: number;
  workflowDefinitionCode: string;
  validNext: ValidTransition[];
}

export function useAssetEngineState(assetId: string | null) {
  return useQuery({
    queryKey: KEY_VALID_TX(assetId),
    enabled: assetId !== null,
    retry: false,
    queryFn: async () => {
      try {
        const res = await api.get<{ assetId: string } & AssetEngineSnapshot>(
          `/api/v2/assets/${encodeURIComponent(assetId!)}/transitions`
        );
        return {
          currentStateCode: res.data.currentStateCode,
          currentStateName: res.data.currentStateName,
          workflowDefinitionId: res.data.workflowDefinitionId,
          workflowDefinitionCode: res.data.workflowDefinitionCode,
          validNext: res.data.validNext,
        } satisfies AssetEngineSnapshot;
      } catch (e: any) {
        // 409 WORKFLOW_NOT_CONFIGURED + 404 (feature off / SGS) -> legacy
        if (e?.response?.status === 409 || e?.response?.status === 404) return null;
        throw e;
      }
    },
  });
}

/**
 * Backward-compatible helper returning just the transition list. Existing
 * call sites in the dropdown stay unchanged.
 */
export function useValidTransitions(assetId: string | null) {
  const q = useAssetEngineState(assetId);
  return {
    ...q,
    data: q.data === undefined ? undefined : q.data === null ? null : q.data.validNext,
  };
}

// ============================================================
// Mutations
// ============================================================

export function useValidateWorkflow() {
  return useMutation({
    mutationFn: async (json: string) => {
      const res = await api.post<WorkflowValidateResponse>(
        '/api/v2/workflows/definitions/validate',
        { json }
      );
      return res.data;
    },
  });
}

export function useImportWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (json: string) => {
      const res = await api.post<WorkflowImportResponse>(
        '/api/v2/workflows/definitions/import',
        { json }
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_DEFS });
    },
  });
}

// P03 — multipart file-upload variants. The browser sends the file as
// raw UTF-8 bytes through FormData; no shell or Python locale ever
// touches it. The backend reads bytes strictly as UTF-8 and rejects
// invalid sequences with ENCODING_NOT_UTF8 — see WorkflowsController
// .ReadUtf8FileAsync. Same response shape as the JSON variants.

function fileFormData(file: File): FormData {
  const fd = new FormData();
  fd.append('file', file, file.name);
  return fd;
}

export function useValidateWorkflowFile() {
  return useMutation({
    mutationFn: async (file: File) => {
      const res = await api.post<WorkflowValidateResponse>(
        '/api/v2/workflows/definitions/validate',
        fileFormData(file),
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      return res.data;
    },
  });
}

export function useImportWorkflowFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const res = await api.post<WorkflowImportResponse>(
        '/api/v2/workflows/definitions/import',
        fileFormData(file),
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      return res.data;
    },
    onSuccess: () => {
      // Same invalidation graph as useImportWorkflow plus the dynamic-modules
      // cache so the left nav updates immediately after a successful import
      // (handoff doc 06 §React Query invalidation graph).
      qc.invalidateQueries({ queryKey: KEY_DEFS });
      qc.invalidateQueries({ queryKey: ['dynamicModules'] });
      qc.invalidateQueries({ queryKey: ['workflowKpi'] });
    },
  });
}

// ===== Phase 8 #13 — instance migration =====================================

export interface WorkflowStateMapping {
  fromStateCode: string;
  toStateCode: string;
}

export interface MigrationRequest {
  fromDefinitionId: number;
  stateMapping?: WorkflowStateMapping[];
  dryRun?: boolean;
}

export interface MigrationResult {
  totalInstances: number;
  migrated: number;
  skippedUnmapped: number;
  errors: string[];
  dryRun: boolean;
}

export function useMigrateInstances() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ toId, body }: { toId: number; body: MigrationRequest }) => {
      const res = await api.post<MigrationResult>(
        `/api/v2/workflows/definitions/${toId}/migrate-instances`,
        body
      );
      return res.data;
    },
    onSuccess: (_data, vars) => {
      // Instance distribution changed; invalidate the KPI summary so
      // dashboards refresh on the target definition.
      qc.invalidateQueries({ queryKey: ['workflow-kpi', vars.toId] });
    },
  });
}

export interface CreateBindingInput {
  assetClassId: number;
  workflowDefinitionId: number;
  clientId?: number | null;
  effectiveFrom?: string | null;
}

export function useCreateWorkflowBinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateBindingInput) => {
      const res = await api.post<{ id: number; alreadyExisted?: boolean }>(
        '/api/v2/workflows/bindings',
        body
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_BINDINGS });
    },
  });
}

export interface ExecuteTransitionInput {
  assetId: string;
  transitionCode: string;
  payload?: unknown;
  /** UUID for idempotent retries (mobile uses this; web typically omits). */
  idempotencyKey?: string;
}

export interface ExecuteTransitionResult {
  assetId: string;
  fromStateCode: string;
  toStateCode: string;
  transitionCode: string;
  workflowDefinitionId: number;
  assetStatusLogId: number | null;
  occurredAt: string;
  dryRun?: boolean;
}

export function useExecuteTransition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      assetId,
      transitionCode,
      payload,
      idempotencyKey,
    }: ExecuteTransitionInput) => {
      const headers: Record<string, string> = {};
      if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
      const res = await api.post<ExecuteTransitionResult>(
        `/api/v2/assets/${encodeURIComponent(assetId)}/transitions/${encodeURIComponent(transitionCode)}`,
        payload ?? null,
        { headers }
      );
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEY_VALID_TX(vars.assetId) });
    },
  });
}

export function useDeleteWorkflowBinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/v2/workflows/bindings/${id}`);
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_BINDINGS });
    },
  });
}
